import { Spacer } from "@hackthenorth/north";
import { Label, Button } from "north.js";
import React from "react";
import { styled } from "twin.macro";

import { useSponsorContext } from "src/shared/contexts";
import { optionsToNorthV2Options } from "src/shared/utils/react-select";
import { SponsorBayFloor1, SponsorBayFloor2 } from "src/static/img";
import {
  PerkStatus,
  SponsorPerksData,
  SponsorPerkType,
} from "src/views/sponsor/perks/types";

import { PerkContainer, PerkError, PerkSelect } from "../components";
import Header from "../Header";
import { getPerkStatusLabel, perkStatusToBadgeColor } from "../helpers";
import { TextDescription } from "../TextComponents";
import { PerkData } from "../usePerkState";

import {
  HUB_LOCATION_VALUES,
  TABLE_LOCATION_VALUES,
  SELECT_TABLE_LOCATION_OPEN_TIME,
  SELECT_TABLE_LOCATION_CLOSE_TIME,
  TABLE_ASSIGNMENTS_RELEASED,
} from "./constants";

export const SELECT_TABLE_LOCATION_VALIDATION_FIELDS: (keyof SponsorPerksData)[] =
  [
    "select_table_location_one",
    "select_table_location_two",
    "select_table_location_three",
  ];

export const SelectTableLocation: React.FC<PerkData> = ({
  getState,
  updateState,
  isReadOnly,
  error,
}) => {
  const { company } = useSponsorContext();
  const IS_GOLD = company?.tier === "gold";

  const tableOne = getState("select_table_location_one");
  const tableTwo = getState("select_table_location_two");
  const tableThree = getState("select_table_location_three");

  const tableOneLabel =
    (IS_GOLD ? HUB_LOCATION_VALUES : TABLE_LOCATION_VALUES).find(
      (option) => option.value === tableOne
    )?.label ?? tableOne;

  const sameChoiceError =
    tableOne === tableTwo || tableOne === tableThree || tableTwo === tableThree;

  const downloadFile = (href: string, filename: string) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSponsorBay = () => {
    downloadFile(SponsorBayFloor1, "HackTheNorth2026_SponsorBayFloor1.png");
    downloadFile(SponsorBayFloor2, "HackTheNorth2026_SponsorBayFloor2.png");
  };

  return (
    <>
      <Header
        title="Select Table Location"
        subtitle="This perk is to choose a table location in the Sponsor Bay."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              getState("select_table_location_status") as PerkStatus | undefined
            )}
            size="sm"
          >
            {getPerkStatusLabel(
              getState("select_table_location_status") as PerkStatus | undefined
            )}
          </Label>
        }
      />

      {Date.now() < new Date(SELECT_TABLE_LOCATION_OPEN_TIME).getTime() ? (
        <>
          <Spacer height={16} />
          <TextDescription>
            Booth selection will be available closer to the event.
          </TextDescription>
        </>
      ) : Date.now() > new Date(SELECT_TABLE_LOCATION_CLOSE_TIME).getTime() ? (
        <>
          <Spacer height={16} />
          <TextDescription>
            {TABLE_ASSIGNMENTS_RELEASED ? (
              <>
                Booth selection has closed. Your booth number is {tableOneLabel}
                .
              </>
            ) : (
              <>
                Booth selection has closed and is no longer available. Your
                assigned booth will be appear here soon.
              </>
            )}
          </TextDescription>
        </>
      ) : (
        <PerkContainer
          description={<Description />}
          id={SponsorPerkType.SELECT_TABLE_LOCATION}
          readOnly={isReadOnly}
        >
          {sameChoiceError && (
            <PerkError
              error={<>Make sure your area selections are different</>}
            />
          )}
          <Spacer height={8} />
          <PerkSelect
            fullWidth
            size="lg"
            onChange={(e) =>
              updateState("select_table_location_one", e.target.value)
            }
            value={getState("select_table_location_one") ?? ""}
            label="Select Table Number (choice 1)*"
            isReadOnly={isReadOnly}
            error={
              sameChoiceError && error?.select_table_location_one ? (
                <>You have duplicate table selections</>
              ) : (
                error?.select_table_location_one &&
                !tableOne && <>This field is required</>
              )
            }
          >
            {optionsToNorthV2Options(
              IS_GOLD ? HUB_LOCATION_VALUES : TABLE_LOCATION_VALUES
            )}
          </PerkSelect>
          <Spacer height={16} />
          <PerkSelect
            fullWidth
            size="lg"
            onChange={(e) =>
              updateState("select_table_location_two", e.target.value)
            }
            value={getState("select_table_location_two") ?? ""}
            label="Select Table Number (choice 2)*"
            isReadOnly={isReadOnly}
            error={
              sameChoiceError && error?.select_table_location_two ? (
                <>You have duplicate table selections</>
              ) : (
                error?.select_table_location_two &&
                !tableTwo && <>This field is required</>
              )
            }
          >
            {optionsToNorthV2Options(
              IS_GOLD ? HUB_LOCATION_VALUES : TABLE_LOCATION_VALUES
            )}
          </PerkSelect>
          <Spacer height={16} />
          <PerkSelect
            fullWidth
            size="lg"
            onChange={(e) =>
              updateState("select_table_location_three", e.target.value)
            }
            value={getState("select_table_location_three") ?? ""}
            label="Select Table Number (choice 3)*"
            isReadOnly={isReadOnly}
            error={
              sameChoiceError && error?.select_table_location_three ? (
                <>You have duplicate table selections</>
              ) : (
                error?.select_table_location_three &&
                !tableThree && <>This field is required</>
              )
            }
          >
            {optionsToNorthV2Options(
              IS_GOLD ? HUB_LOCATION_VALUES : TABLE_LOCATION_VALUES
            )}
          </PerkSelect>
          <Spacer height={48} />

          <ImageContainer>
            <Button color="primary" onClick={handleDownloadSponsorBay}>
              Download Sponsor Bay Map To Zoom In
            </Button>

            <Spacer height={16} />
            <img src={SponsorBayFloor2} alt="sponsor-bay-floor-2" />
            <Spacer height={16} />
            <img src={SponsorBayFloor1} alt="sponsor-bay-floor-1" />
            <Spacer height={8} />
          </ImageContainer>
        </PerkContainer>
      )}
    </>
  );
};

const Description = () => (
  <TextDescription>
    Below is the floorplan of our Sponsor Bay for this year&apos;s Hack the
    North, along with numbered tables. Select the table you want by choosing the
    table number from the dropdown menu below. Note that the table location is
    offered on a first-come, first-serve basis.
  </TextDescription>
);

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
